import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Step,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const IncidentsAndSafetyAlerts = () => (
  <Page
    title="Incidents and Safety Alerts"
    intro="Anyone can report an incident. The Governance team then investigates it, records witness statements and corrective actions, and tracks it through to closure. Safety alerts arrive automatically from the MHRA for governance review."
  >
    <Section title="Reporting an incident">
      <p>
        Open <DocLink url="/report-incident" text="Report Incident" />, read
        the guidance page, and work through the eight-step wizard:
      </p>
      <Step number={1} title="Details">
        <p>
          When and where it happened, and the type of incident (Clinical,
          Patient Experience, Operational, Vehicle &amp; Transport, Equipment
          &amp; Supplies, Premises, Staff Conduct, Staff Welfare, Governance /
          Process, External / Third Party, or Other).
        </p>
      </Step>
      <Step number={2} title="Description">
        <p>What happened, and any initial actions taken.</p>
      </Step>
      <Step number={3} title="People">
        <p>Add anyone involved, with their role and contact details.</p>
      </Step>
      <Step number={4} title="Vehicles">
        <p>Add any vehicles involved.</p>
      </Step>
      <Step number={5} title="Equipment">
        <p>Add any related equipment or asset.</p>
      </Step>
      <Step number={6} title="Medicines">
        <p>
          Medicines are handled in two ways. Pick medicines to actually remove
          from a specific bag (with a quantity — for example accidental
          destruction), and separately reference any other medicines involved
          without forcing a removal. Removals immediately mark that stock as
          disposed and write a medicine log entry linked to the incident.
        </p>
      </Step>
      <Step number={7} title="Attachments">
        <p>Attach photos or documents.</p>
      </Step>
      <Step number={8} title="Characteristics and submit">
        <p>
          Answer the incident characteristic questions — whether it is a
          clinical incident, a safeguarding incident, whether CPR was used,
          whether it involves controlled drugs, manual handling, infection
          prevention and control, or sharps / needlestick — then submit. The
          incident is given the next incident number automatically.
        </p>
      </Step>
      <Info title="Who can do this?">
        Any signed-in user can report an incident. Reviewing and managing
        incidents requires the Governance team (site administrators always
        have access).
      </Info>
    </Section>

    <Section title="Managing an incident">
      <p>
        Governance users review incidents from{' '}
        <DocLink url="/incidents" text="Incidents" />. The list is filterable
        by date and status, and by default shows incidents that are still
        active. Open an incident to work it through its tabs:
      </p>
      <Screenshot url="governance/incidents-list.png">
        The incident list filtered to active incidents, each with its number,
        type, status, and assignee.
      </Screenshot>
      <Bullets>
        <Bullet>
          <strong>Overview</strong> — the reported details (date and time,
          type, location, description, and initial actions) can be corrected
          during review, for example to fix a mistyped date or re-classify the
          incident type. Every correction is recorded in the audit history.
        </Bullet>
        <Bullet>
          <strong>People &amp; Involvement</strong> — everyone involved in the
          incident, each opened in a popup to review and update their details
          and involvement.
        </Bullet>
        <Bullet>
          <strong>Status</strong> — incidents start as <strong>New</strong>{' '}
          and move through <strong>In Progress</strong>,{' '}
          <strong>Waiting</strong>, <strong>Awaiting Witness</strong>, or{' '}
          <strong>Blocked</strong> to <strong>Completed</strong> or{' '}
          <strong>Closed – No Action Required</strong>. Once moved off New, an
          incident cannot be set back to New. Awaiting Witness is set for you
          when you issue an external witness statement link (see below).
        </Bullet>
        <Bullet>
          <strong>Assigned To</strong> — the responsible manager for the
          investigation.
        </Bullet>
        <Bullet>
          <strong>Hidden From</strong> — staff who must not see the incident
          (for example when they are the subject of it). Hidden staff cannot
          open the incident or see it in the list, and you cannot hide an
          incident from yourself.
        </Bullet>
        <Bullet>
          <strong>Investigation Findings</strong> — result, actual impact, and
          a structured what / why / how it happened narrative, plus actions
          taken.
        </Bullet>
        <Bullet>
          <strong>Witness Statements</strong> — record each witness's name,
          contact details, involvement, statement, and statement date/time
          yourself, or send someone outside the organisation a secure link so
          they can write their own statement (see{' '}
          <em>External witness statements</em> below). Statements that came in
          through a link are labelled as such on the incident and the PDF.
        </Bullet>
        <Bullet>
          <strong>Corrective Actions</strong> — Duty of Candour, assessed
          impact, lessons learned, risk register additions, recommendations,
          and actions required. If the review concludes there is nothing to
          change, switch on <em>No corrective actions or learning required</em>{' '}
          and give a short reason — the incident is then excluded from the CQC
          Centre "no learning recorded" warning and the decision appears on
          the PDF and audit trail.
        </Bullet>
        <Bullet>
          <strong>Notes</strong> — a comment thread for the investigating
          team.
        </Bullet>
        <Bullet>
          <strong>History</strong> — the incident's audit trail: every change
          made during review, witness link confirmations, and status moves.
        </Bullet>
      </Bullets>
      <Info title="PDF export and linked feedback">
        The full incident (including witness statements and corrective
        actions) can be exported as a PDF from the incident page. Incidents
        raised from patient feedback show a banner linking back to the
        original feedback.
      </Info>
    </Section>

    <Section title="External witness statements">
      <p>
        When a witness is not an AmbuNet user — a member of the public, a
        venue steward, a relative — you can invite them to give their
        statement through a private link instead of transcribing it yourself.
        Open the incident's <strong>Witness Statements</strong> tab and choose{' '}
        <strong>Request a statement</strong>:
      </p>
      <Step number={1} title="Who and what">
        <p>
          Enter the witness's name, optionally their email address and
          involvement, and a short brief telling them what you would like
          them to cover. The brief is shown to the witness, so keep it to the
          minimum they need and never include names or details of patients,
          staff or anyone else.
        </p>
      </Step>
      <Step number={2} title="Expiry and delivery">
        <p>
          Choose how long the link stays open (7, 14 or 30 days). AmbuNet can
          email the link to the witness for you, or you can copy it and send
          it through a channel you trust. By default the incident is moved to{' '}
          <strong>Awaiting Witness</strong> while you wait.
        </p>
      </Step>
      <Step number={3} title="Data protection acknowledgement">
        <p>
          Before the link is created you must confirm four statements: that
          the person is a genuine witness, that you will share the link only
          with them, that the brief contains no third-party personal data,
          and that you understand the witness sees a privacy notice and the
          link expires. Your confirmation is recorded on the incident's
          History tab.
        </p>
      </Step>
      <Step number={4} title="The witness submits">
        <p>
          The witness opens the link without logging in. They see your
          organisation's name, the incident number and date, your brief and a
          privacy notice, then write their statement, give contact details,
          confirm they have read the notice and declare the statement is
          true. The link can be used once and is then closed.
        </p>
      </Step>
      <Bullets>
        <Bullet>
          <strong>You are told when it arrives</strong> — whoever issued the
          link and the incident's assigned manager receive an{' '}
          <em>Incident witness statements</em> notification (email, push or
          app inbox, according to their notification preferences). If the
          incident was Awaiting Witness and nothing else is outstanding, it
          returns to its previous status automatically.
        </Bullet>
        <Bullet>
          <strong>Expiry</strong> — links that lapse without a statement are
          closed automatically and the same people are notified so they can
          issue a new one. You can also re-send the email (optionally
          extending the expiry) or revoke a link at any time from the tab.
        </Bullet>
        <Bullet>
          <strong>What the witness can see</strong> — only the incident
          number and date, their own name as you entered it, your brief and
          the privacy notice. Nothing else from the incident record leaves
          AmbuNet.
        </Bullet>
        <Bullet>
          <strong>Retention</strong> — the statement becomes part of the
          incident record. The email address you used to send the link is
          deleted automatically 90 days after the request closes.
        </Bullet>
      </Bullets>
      <Info title="Who can do this?">
        Issuing, re-sending and revoking witness links requires the Governance
        team, and only for incidents you are able to see.
      </Info>
    </Section>

    <Section title="Safety alerts">
      <p>
        AmbuNet checks the GOV.UK MHRA drug and device alerts feed every night
        and adds any recent alerts to{' '}
        <DocLink url="/safety-alerts" text="Safety Alerts" /> automatically —
        you do not write safety alerts yourself. New alerts wait on the{' '}
        <strong>To review</strong> tab, and Governance users review each one:
      </p>
      <Screenshot url="governance/safety-alerts.png">
        The Safety Alerts page with MHRA alerts waiting on the To review tab.
      </Screenshot>
      <Bullets>
        <Bullet>
          Use <strong>Relevant — record action</strong> to accept an alert
          that applies to your organisation and record the action taken.
        </Bullet>
        <Bullet>
          <strong>Dismiss</strong> an alert that does not apply using{' '}
          <strong>Not relevant</strong>. Dismissed alerts are removed from
          Safety Alerts and will not come back from the nightly check. Use{' '}
          <strong>Undo</strong> straight away if you dismissed one by mistake.
        </Bullet>
        <Bullet>
          Optionally publish an accepted alert as an announcement that staff
          must acknowledge — it appears in{' '}
          <DocLink url="/announcements" text="Announcements" /> with a link to
          the MHRA notice.
        </Bullet>
        <Bullet>
          Add follow-up tasks to an accepted alert to assign the work to a
          person or team.
        </Bullet>
      </Bullets>
      <p>
        The <strong>Accepted</strong> tab lists every alert you have accepted,
        with the action taken, who accepted it and when, and any follow-up
        tasks. You can search it, and open an alert to edit the action taken.
      </p>
      <Info title="Who can do this?">
        Reviewing safety alerts requires the Governance team. Incidents and
        safety alerts are not subscription-gated.
      </Info>
    </Section>

    <Section title="Feedback review">
      <p>
        Patient feedback has its own review workflow — see{' '}
        <DocLink
          docsPage
          url="/clinical-records/patient-feedback"
          text="Patient Feedback"
        />{' '}
        for the public submission route and the internal{' '}
        <DocLink url="/feedback" text="Feedback Review" /> workflow. Feedback
        that needs escalating can be raised as an incident, which stays linked
        to the original feedback.
      </p>
    </Section>
  </Page>
)

export default IncidentsAndSafetyAlerts
